import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowRight, Linkedin, Github, Mail } from 'lucide-react';
import { useCursorContext } from '../hooks/useCursorContext';
import { nikoInfo } from '../data/portfolioData';

const socialIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  LinkedIn: Linkedin,
  GitHub: Github,
  Email: Mail,
};

gsap.registerPlugin(ScrollTrigger);

export function ContactSection() {
  const containerRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const buttonRef = useRef<HTMLAnchorElement>(null);
  const [buttonPosition, setButtonPosition] = useState({ x: 0, y: 0 });
  const { setCursorState } = useCursorContext();

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Title reveal
      gsap.fromTo(
        titleRef.current,
        { y: 100, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: containerRef.current,
            start: 'top 60%',
          },
        }
      );
    }, containerRef);

    return () => ctx.revert();
  }, []);

  // Magnetic button effect
  const handleMouseMove = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const deltaX = (e.clientX - centerX) * 0.3;
    const deltaY = (e.clientY - centerY) * 0.3;

    setButtonPosition({ x: deltaX, y: deltaY });
  };

  const handleMouseLeave = () => {
    setButtonPosition({ x: 0, y: 0 });
    setCursorState('default');
  };

  return (
    <section
      id="contact"
      ref={containerRef}
      className="relative py-32 md:py-48 px-8 md:px-12 min-h-screen flex items-center"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-emerald-500/10 rounded-full blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto w-full">
        {/* Section label */}
        <div className="mb-12">
          <span className="text-xs text-white/30 tracking-[0.3em] uppercase">
            Kontakt
          </span>
        </div>

        {/* Main CTA */}
        <h2
          ref={titleRef}
          className="text-5xl md:text-7xl lg:text-8xl font-light text-white leading-tight mb-16"
        >
          Lass uns
          <br />
          <span className="text-emerald-500">zusammenarbeiten</span>
        </h2>

        {/* Email button */}
        <div className="mb-16">
          <a
            ref={buttonRef}
            href={`mailto:${nikoInfo.email}`}
            className="inline-flex items-center gap-4 px-8 py-6 border border-white/20 rounded-full group hover:border-emerald-500 transition-colors"
            style={{
              transform: `translate(${buttonPosition.x}px, ${buttonPosition.y}px)`,
              transition: buttonPosition.x === 0 ? 'transform 0.5s ease-out' : 'none',
            }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onMouseEnter={() => setCursorState('hovering')}
          >
            <span className="text-xl md:text-2xl text-white/80 group-hover:text-emerald-500 transition-colors">
              {nikoInfo.email}
            </span>
            <ArrowRight className="w-6 h-6 text-white/40 group-hover:text-emerald-500 group-hover:translate-x-2 transition-all" />
          </a>
        </div>

        {/* Contact info grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <div>
            <h4 className="text-xs text-white/30 tracking-[0.3em] uppercase mb-3">
              Standort
            </h4>
            <p className="text-white/60">{nikoInfo.location}</p>
          </div>
          <div>
            <h4 className="text-xs text-white/30 tracking-[0.3em] uppercase mb-3">
              Website
            </h4>
            <a
              href={`https://${nikoInfo.website}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/60 hover:text-emerald-500 transition-colors"
              onMouseEnter={() => setCursorState('hovering')}
              onMouseLeave={() => setCursorState('default')}
            >
              {nikoInfo.website}
            </a>
          </div>
          <div>
            <h4 className="text-xs text-white/30 tracking-[0.3em] uppercase mb-3">
              Erfahrung
            </h4>
            <p className="text-white/60">{nikoInfo.experience} Jahre</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between pt-12 border-t border-white/10">
          {/* Socials */}
          <div className="flex gap-6 mb-8 md:mb-0">
            {nikoInfo.socials.map((social) => {
              const Icon = socialIcons[social.name];
              return (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-white/40 hover:text-emerald-500 transition-colors tracking-wider"
                  onMouseEnter={() => setCursorState('hovering')}
                  onMouseLeave={() => setCursorState('default')}
                >
                  {Icon && <Icon className="w-4 h-4" />}
                  {social.name}
                </a>
              );
            })}
          </div>

          {/* Copyright & Legal */}
          <div className="flex items-center gap-8">
            <span className="text-xs text-white/20">
              © {new Date().getFullYear()} NK Design
            </span>
            <Link
              to="/impressum"
              className="text-xs text-white/20 hover:text-emerald-500 transition-colors"
              onMouseEnter={() => setCursorState('hovering')}
              onMouseLeave={() => setCursorState('default')}
            >
              Impressum
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
